import React, { useEffect } from 'react';
import { arrayOf, bool, string } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { isMainSearchTypeKeywords } from '../../util/search';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn, NamedLink, IconSpinner } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { fetchAllStates, getStatesData } from '../../ducks/strapi.duck';
import { isArrayLength } from '../../util/genericHelpers';

import css from './SeoCategoriesPage.module.css';

const SeoCategoriesPageComponent = ({ scrollingDisabled, intl }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchAllStates());
  }, []);

  const { statesData, searchInProgress } = useSelector(getStatesData);

  const schemaTitle = intl.formatMessage({ id: 'SeoCategoriesPage.headerSubtitle' });
  const schemaDescription = intl.formatMessage({ id: 'SeoCategoriesPage.headerDescription' });

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        image: [schemaDescription],
      }}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={false} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        <div className={css.pageRoot}>
          <div className={css.pageHeader}>
            <span>
              {intl.formatMessage({
                id: 'SeoCategoriesPage.headerTitle',
                defaultMessage: 'Categories',
              })}
            </span>
            <h1>
              {intl.formatMessage({
                id: 'SeoCategoriesPage.headerSubtitle',
              })}
            </h1>
            <p>
              {intl.formatMessage({
                id: 'SeoCategoriesPage.headerDescription',
              })}
            </p>
          </div>
          <div className={css.otherLinksList}>
            {searchInProgress ? (
              <div className={css.iconSpinner}>
                <IconSpinner />
              </div>
            ) : null}
            <ul>
              {isArrayLength(statesData) ? (
                statesData.map(s => (
                  <li key={s?.slug}>
                    <NamedLink name="StateListsPage" params={{ state: s?.slug }}>
                      {s?.title}
                    </NamedLink>
                  </li>
                ))
              ) : (
                <></>
              )}
            </ul>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SeoCategoriesPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  marketplaceName: string.isRequired,
  isKeywordSearch: bool.isRequired,
  intl: intlShape.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSeoCategoriesPage = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const history = useHistory();
  const intl = useIntl();

  return (
    <SeoCategoriesPageComponent
      routeConfiguration={routeConfiguration}
      marketplaceName={config.marketplaceName}
      isKeywordSearch={isMainSearchTypeKeywords(config)}
      history={history}
      intl={intl}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const SeoCategoriesPage = compose(connect(mapStateToProps))(EnhancedSeoCategoriesPage);

export default SeoCategoriesPage;
